import React, { useState, useEffect } from "react";
import { onAuthStateChanged, getRedirectResult } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { auth, db } from "./firebase";
import Login from "./components/Login";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import ShopDashboard from "./components/ShopDashboard";
import "./App.css";

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : Promise.resolve(null);

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Helper function to create user document
  const createUserDocument = async (user, additionalData = {}) => {
    try {
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        // Create new user document
        const userData = {
          email: user.email,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          isAdmin: false, // Default to false, admin needs to be set manually
          createdAt: new Date(),
          lastLogin: new Date(),
          ...additionalData
        };
        
        await setDoc(userRef, userData);
        return userData;
      } else {
        // Update last login
        await setDoc(userRef, { lastLogin: new Date() }, { merge: true });
        return userSnap.data();
      }
    } catch (error) {
      console.error("Error creating/updating user document:", error);
      throw error;
    }
  };

  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          await createUserDocument(result.user);
        }
      } catch (error) {
        console.error('Error handling redirect result:', error);
        setError("Er is een fout opgetreden bij het inloggen. Probeer het opnieuw.");
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Get user data from Firestore
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            
            // Check if user has admin access (isAdmin, isSuperAdmin, or adminShopId)
            // Allow access if user has any admin rights OR if they have an adminShopId (shop owner)
            if (!data.isAdmin && !data.isSuperAdmin && !data.adminShopId) {
              setError("Je hebt geen toegang tot deze portal. Alleen beheerders kunnen inloggen. Neem contact op met de beheerder om je account te activeren.");
              await auth.signOut();
            } else {
            }
          } else {
            // User document doesn't exist - this could be a new shop owner
            // Check if there's a shop document for this user
            const shopDoc = await getDoc(doc(db, "shops", user.uid));
            if (shopDoc.exists()) {
              // User is a shop owner but user document is missing
              // Create the user document automatically
              const userData = await createUserDocument(user, { 
                isAdmin: true, 
                adminShopId: user.uid 
              });
              setUserData(userData);
            } else {
              // Neither user nor shop document exists - this is likely a new user
              // Show a more helpful message for new users
              setError("Welkom! Je account is succesvol aangemaakt, maar je hebt nog geen toegang tot de portal. Neem contact op met de beheerder om je account te activeren als beheerder.");
              await auth.signOut();
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setError("Er is een fout opgetreden bij het laden van je gegevens. Probeer het opnieuw.");
        }
      }
      
      setLoading(false);
    });

    // Handle redirect result first
    handleRedirectResult();

    return () => unsubscribe();
  }, []);

  const handleLogin = () => {
    // Login successful, user state will be updated by onAuthStateChanged
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      setUserData(null);
      setError("");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div className="card">
          <div className="card-body text-center">
            <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
            <p>Laden...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }}>
        <div className="card">
          <div className="card-header">
            <h2>Fout</h2>
          </div>
          <div className="card-body">
            <div className="alert alert-danger">
              {error}
            </div>
            
            <button
              onClick={handleLogout}
              className="btn btn-primary w-full"
            >
              Terug naar login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="App">
        {user && userData ? (
          <>
            <button
              onClick={handleLogout}
              className="btn btn-danger"
              style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 9999 }}
            >
              Uitloggen
            </button>
            {userData.isSuperAdmin ? (
              // Super Admin Dashboard - Only for super admins
              <SuperAdminDashboard />
            ) : userData.isAdmin && userData.adminShopId ? (
              // Shop Admin Dashboard - For shop owners
              <ShopDashboard 
                shopId={userData.adminShopId} 
                onBack={handleLogout}
              />
            ) : userData.isAdmin && !userData.adminShopId ? (
              // Regular Admin Dashboard - Only for regular admins (not shop owners)
              <SuperAdminDashboard />
            ) : (
              // Fallback - should not happen due to auth check above
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div className="card">
                  <div className="card-body text-center">
                    <p>Geen toegang</p>
                    <button onClick={handleLogout} className="btn btn-primary">
                      Uitloggen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </div>
    </Elements>
  );
}

export default App;
